'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const pendingItems = [
  {
    id: 'rec-pending-001',
    title: 'Meeting Minutes — 14 April 1887',
    category: 'Lodge Records',
    uploadedBy: 'Contributor Token',
    uploadedAt: '02 May 2026',
    aiConfidence: 72,
    urgent: true,
    fileType: 'JPG',
  },
  {
    id: 'rec-pending-002',
    title: 'Treasurer\'s Report — March 1892',
    category: 'Lodge Records',
    uploadedBy: 'R. Sinclair',
    uploadedAt: '01 May 2026',
    aiConfidence: 85,
    urgent: true,
    fileType: 'PDF',
  },
  {
    id: 'rec-pending-003',
    title: 'Past Master\'s Apron — circa 1830',
    category: 'Regalia & Textiles',
    uploadedBy: 'Contributor Token',
    uploadedAt: '30 Apr 2026',
    aiConfidence: 91,
    urgent: false,
    fileType: 'JPG',
  },
  {
    id: 'rec-pending-004',
    title: 'Cornerstone Photograph — Lodge Hall, 1878',
    category: 'Building & Property',
    uploadedBy: 'H. MacLeod',
    uploadedAt: '29 Apr 2026',
    aiConfidence: 88,
    urgent: false,
    fileType: 'JPG',
  },
  {
    id: 'rec-pending-005',
    title: 'Initiation Record — W. MacPherson, 1903',
    category: 'Member History',
    uploadedBy: 'R. Sinclair',
    uploadedAt: '28 Apr 2026',
    aiConfidence: 79,
    urgent: true,
    fileType: 'PDF',
  },
];

export default function DashboardPendingQueue() {
  const [dismissed, setDismissed] = useState<string[]>([]);
  const visible = pendingItems.filter((i) => !dismissed.includes(i.id));

  return (
    <div className="masonic-card overflow-hidden">
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-lg font-semibold" style={{ color: 'var(--foreground)' }}>
            Pending Review Queue
          </h2>
          <span
            className="status-badge status-pending"
          >
            {visible.length} items
          </span>
        </div>
        <Link
          href="/document-transcription-review"
          className="text-sm font-semibold flex items-center gap-1.5 hover:underline"
          style={{ color: 'var(--accent)' }}
        >
          Review all
          <Icon name="ArrowRightIcon" size={15} />
        </Link>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              {['Record Title', 'Category', 'Uploaded By', 'Date', 'AI Confidence', 'Actions'].map((col) => (
                <th
                  key={`th-${col}`}
                  className="text-left px-4 py-3 font-semibold uppercase tracking-wider text-xs"
                  style={{ color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((item, idx) => (
              <tr
                key={item.id}
                className="transition-colors hover:bg-muted/50"
                style={{
                  borderBottom: idx < visible.length - 1 ? '1px solid var(--border)' : 'none',
                  background: item.urgent ? 'rgba(254, 243, 199, 0.3)' : undefined,
                }}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {item.urgent && (
                      <span title="Urgent">
                        <Icon name="ExclamationTriangleIcon" size={14} style={{ color: 'var(--warning)' } as React.CSSProperties} />
                      </span>
                    )}
                    <span className="font-medium truncate max-w-xs" style={{ color: 'var(--foreground)' }}>
                      {item.title}
                    </span>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded font-mono"
                      style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                    >
                      {item.fileType}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>
                    {item.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {item.uploadedBy}
                </td>
                <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                  {item.uploadedAt}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full max-w-16" style={{ background: 'var(--border)' }}>
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${item.aiConfidence}%`,
                          background:
                            item.aiConfidence >= 85
                              ? 'var(--success)'
                              : item.aiConfidence >= 70
                              ? 'var(--accent)'
                              : 'var(--danger)',
                        }}
                      />
                    </div>
                    <span
                      className="text-xs font-semibold tabular-nums"
                      style={{
                        color:
                          item.aiConfidence >= 85
                            ? 'var(--success)'
                            : item.aiConfidence >= 70
                            ? 'var(--warning)'
                            : 'var(--danger)',
                      }}
                    >
                      {item.aiConfidence}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Link
                      href="/document-transcription-review"
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Review transcription"
                    >
                      <Icon name="DocumentMagnifyingGlassIcon" size={16} style={{ color: 'var(--primary)' } as React.CSSProperties} />
                    </Link>
                    <button
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Dismiss from queue"
                      onClick={() => setDismissed((d) => [...d, item.id])}
                    >
                      <Icon name="XMarkIcon" size={16} style={{ color: 'var(--muted-foreground)' } as React.CSSProperties} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {visible.length === 0 && (
          <div className="py-12 text-center">
            <Icon name="CheckCircleIcon" size={40} style={{ color: 'var(--success)', margin: '0 auto 12px' } as React.CSSProperties} />
            <p className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              All caught up
            </p>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
              No records are waiting for your review.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}